import React, { useState } from 'react';
import { Box, ButtonBase, Link, Typography } from '@mui/material';
import { RiArrowDownSLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import { getTranslation } from 'common/modules/translations';
import { ShopManualOnlinePaymentMethodObject } from 'common/modules/payments/types';
import { useTranslation } from 'services/localization';
import ManualPaymentMethodDialog from './ManualPaymentMethodDialog';
import { SxProps, Theme } from '@mui/system';

interface Props {
	value: ShopManualOnlinePaymentMethodObject;
	onChange: (value: ShopManualOnlinePaymentMethodObject) => void;
	options: ShopManualOnlinePaymentMethodObject[];
	variant?: 'select' | 'link' | 'text';
	sx?: SxProps<Theme>;
}
const ManualPaymentMethodSelect = (props: Props) => {
	const { options, value, onChange, variant = 'select', sx } = props;
	const { t } = useTranslation();
	const lang = useSelector(LanguageSelectors.language);
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);

	const getSelectedOptionLabel = () => {
		if (value.id === 'PAY_STORE') return t('payments.payInstore');
		return getTranslation(value.details.label ?? { def: '' }, lang) || value.details.label?.def;
	};

	const renderSelect = () => {
		switch (variant) {
			case 'select': {
				return (
					<ButtonBase
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center',
							...sx,
						}}
						onClick={() => setDialogOpen(true)}
						focusRipple
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								textAlign: 'left',
							}}
						>
							<Typography variant="caption" color="secondary">
								{t('common:payment.payment', 'Payment')}
							</Typography>
							<Typography variant="body1">{getSelectedOptionLabel()}</Typography>
						</Box>
						<RiArrowDownSLine size={24} />
					</ButtonBase>
				);
			}
			case 'link':
				return (
					<Link
						display="inline-block"
						onClick={() => setDialogOpen(true)}
						component="button"
						sx={{ maxWidth: '100%', ...sx }}
					>
						<Typography
							variant="body2"
							sx={{
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								textOverflow: 'ellipsis',
							}}
						>
							{getSelectedOptionLabel()}
						</Typography>
					</Link>
				);
			case 'text':
			default: {
				return (
					<Typography
						variant="body2"
						color="secondary"
						sx={{
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
							...sx,
						}}
					>
						{getSelectedOptionLabel()}
					</Typography>
				);
			}
		}
	};

	return (
		<>
			{renderSelect()}
			{dialogOpen && (
				<ManualPaymentMethodDialog
					currentValue={value}
					options={options}
					isOpen={dialogOpen}
					onClose={() => setDialogOpen(false)}
					onChange={onChange}
				/>
			)}
		</>
	);
};

export default ManualPaymentMethodSelect;
